import { Chip, ChipProps, Theme } from '@mui/material';
import { Component } from 'react';
import { StatusStateType } from '@synergy/models';
import { ThemeExtension } from '@synergy/theme';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { getChipColor } from '@synergy/utils';

/**
 * What the component used to hardcode. Themes that do not carry the contract —
 * the shop's, for one — keep exactly the chip they have today.
 */
const LEGACY_CHIP = {
    height: '1.5rem',
    borderRadius: '24px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.5px',
    labelMinWidth: 120,
    listTextTransform: 'uppercase' as const,
    listTextAlign: 'center' as const,
    listWidth: '100%' as const,
};

const styles = (theme: Theme) => {
    const chip = theme.chip ?? LEGACY_CHIP;

    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { appearance, textDecorationSkipInk, transformBox, ...themeDefault } = theme.status.default;
    const { appearance: a1, textDecorationSkipInk: t1, transformBox: b1, ...themeError } = theme.status.error;
    const { appearance: a2, textDecorationSkipInk: t2, transformBox: b2, ...themeWarning } = theme.status.warning;
    const { appearance: a3, textDecorationSkipInk: t3, transformBox: b3, ...themeSuccess } = theme.status.success;
    const { appearance: a4, textDecorationSkipInk: t4, transformBox: b4, ...themePrimary } = theme.status.primary;
    const { appearance: a5, textDecorationSkipInk: t5, transformBox: b5, ...themeInfo } = theme.status.info;
    /* eslint-enable */

    return createStyles({
        root: {
            // Shape comes from the theme, so the same chip reads as a legacy
            // pill in the old apps and as a soft PALO pill in the admin.
            height: chip.height,
            borderRadius: chip.borderRadius,
            fontSize: chip.fontSize,
            fontWeight: chip.fontWeight,
            letterSpacing: chip.letterSpacing,
            lineHeight: 'normal',

            '& > span': {
                minWidth: chip.labelMinWidth,
                textAlign: chip.listTextAlign,
            },
        },
        list: {
            textTransform: chip.listTextTransform,
            width: chip.listWidth,
            textAlign: chip.listTextAlign,
            '& > span > span': {
                whiteSpace: 'pre',
            },
        },
        filter: {
            height: '36px',
            padding: '8px',
            borderRadius: '18px',

            '& .MuiChip-deleteIcon': {
                fontSize: '18px',
                marginLeft: '0px',
                color: 'white',
            },

            '& > span': {
                minWidth: 0,
            },
        },
        tag: {
            // A tag is the same pill as a status chip, only without the colour,
            // so it takes its size from the same contract instead of its own.
            ...(theme.chip
                ? { margin: '2px 3px 2px 0', height: chip.height, fontSize: chip.fontSize }
                : { margin: '8px 4px', height: 32, fontSize: 12 }),
            fontWeight: 400,
            letterSpacing: 'unset',

            '& > span ': {
                minWidth: 0,
                padding: theme.chip ? '0 8px' : '0 0.75rem',
            },

            '& .MuiChip-deleteIcon': {
                marginLeft: '-0.25rem',
                marginRight: '0.75rem',
            },
        },
        default: {
            ...themeDefault,
            '&:focus': {
                backgroundColor: ThemeExtension.status.default.backgroundColor,
            },
        },
        info: {
            ...themeInfo,
            '&:focus': {
                backgroundColor: ThemeExtension.status.info.backgroundColor,
            },
        },
        error: {
            ...themeError,
            '&:focus': {
                backgroundColor: ThemeExtension.status.error.backgroundColor,
            },
        },
        warning: {
            ...themeWarning,
            '&:focus': {
                backgroundColor: ThemeExtension.status.warning.backgroundColor,
            },
        },
        success: {
            ...themeSuccess,
            '&:focus': {
                backgroundColor: ThemeExtension.status.success.backgroundColor,
            },
        },
        primary: {
            ...themePrimary,
            '&:focus': {
                backgroundColor: ThemeExtension.status.primary.backgroundColor,
            },

            '& .MuiChip-deleteIcon': {
                color: 'rgba(255, 255, 255, 0.7)',

                '&:hover': {
                    color: 'white',
                },
            },
        },
    });
};

interface PoolChipPropsBase extends WithStyles<typeof styles> {
    type?: keyof ReturnType<typeof styles>;
    listItem?: boolean;
    filterItem?: boolean;
    status?: StatusStateType;
}

type PoolChipProps = PoolChipPropsBase & ChipProps;

export const PoolChip = withStyles(styles)(
    class PoolChipBase extends Component<PoolChipProps> {
        private static translateStatusToClass(status: StatusStateType): keyof ReturnType<typeof styles> {
            // TODO: refactor
            return getChipColor(status) as keyof ReturnType<typeof styles>;
        }

        private static getDisplayType(props: PoolChipProps): keyof ReturnType<typeof styles> {
            if (props.type) {
                return props.type;
            } else if (props.status) {
                return this.translateStatusToClass(props.status);
            }
            return 'default';
        }

        private static getClassNames(props: PoolChipProps) {
            return `${props.classes.root} ${props.classes[this.getDisplayType(props)]} ${
                props.listItem ? props.classes.list : ''
            }  ${props.filterItem ? props.classes.filter : ''} ${props.className || ''}`;
        }

        public override render() {
            // remove not forwarded props
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { type, listItem, filterItem, status, classes, ...baseChipProps } = this.props;

            return <Chip {...baseChipProps} className={PoolChipBase.getClassNames(this.props)} />;
        }
    }
);
